import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import CloseIcon from '@mui/icons-material/Close'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Radio,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getSupportReportRecipients,
  sendSupportReportToPractitioner,
} from 'src/supportPortal/services/supportBookedEventActionsService'

// Support forwards the session's report to another practitioner in the patient's care team (e.g. a
// therapy report to the patient's doctor). Recipients are grouped into role sub-tabs (Doctor / Therapist
// / Screener); the pre-step consultation's doctor is flagged for screening reports. Delivered FROM AUMHUM.
export default function SupportSendReportToPractitionerDialog({ open, onClose, contactContext, onSent }) {
  const clinicalDocument = contactContext?.clinicalDocument || {}
  const reportType = clinicalDocument?.reportType
  const documentName = clinicalDocument?.documentName || 'report'
  const bookedEventId = contactContext?.bookedEventId
  const patientName = contactContext?.patientName || 'the patient'

  const [loading, setLoading] = useState(true)
  const [recipients, setRecipients] = useState([])
  const [targetRoles, setTargetRoles] = useState([])
  const [activeRole, setActiveRole] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [deliveryMode, setDeliveryMode] = useState('link')
  const [whatsapp, setWhatsapp] = useState(true)
  const [email, setEmail] = useState(true)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!open || !reportType || !bookedEventId) {
      return undefined
    }
    let active = true
    setLoading(true)
    setSelectedId('')
    setDeliveryMode('link')
    setWhatsapp(true)
    setEmail(true)
    getSupportReportRecipients(reportType, bookedEventId).then((result) => {
      if (!active) {
        return
      }
      const list = result.ok ? result.recipients : []
      setRecipients(list)
      const roles = result.ok && result.targetRoles.length ? result.targetRoles : rolesFromRecipients(list)
      setTargetRoles(roles)
      // First tab that actually has a recipient (else the first target role).
      const firstWithPeople = roles.find((role) => list.some((r) => (r.roles || []).includes(role)))
      setActiveRole(firstWithPeople || roles[0] || '')
      setLoading(false)
    })
    return () => {
      active = false
    }
  }, [open, reportType, bookedEventId])

  const recipientsForRole = useMemo(
    () => recipients.filter((r) => !activeRole || (r.roles || []).includes(activeRole)),
    [recipients, activeRole],
  )

  const selected = recipients.find((r) => r.practitionerId === selectedId)

  async function handleSend() {
    if (!selected) {
      toast.error('Pick a practitioner to send to')
      return
    }
    const channels = [whatsapp ? 'whatsapp' : null, email ? 'email' : null].filter(Boolean)
    if (!channels.length) {
      toast.error('Pick at least one channel')
      return
    }
    setSending(true)
    const result = await sendSupportReportToPractitioner(reportType, bookedEventId, selected.practitionerId, {
      deliveryMode,
      channels,
    })
    setSending(false)
    if (result.ok) {
      toast.success(`${documentName} sent to ${result.recipientName || selected.name}`)
      onSent?.(result)
      onClose?.()
      return
    }
    toast.error(result.message || 'Could not send the report.')
  }

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)', color: '#fff' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ShareOutlinedIcon sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Send {documentName}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5, wordBreak: 'break-word' }}>
              To a practitioner in {patientName}&apos;s care team
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={sending} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={26} />
          </Stack>
        ) : recipients.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No eligible {targetRoles.join(' / ') || 'practitioner'} found in {patientName}&apos;s care team yet.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            {targetRoles.length > 1 && (
              <Tabs
                value={activeRole}
                onChange={(_, value) => setActiveRole(value)}
                variant="fullWidth"
                sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 700 } }}
              >
                {targetRoles.map((role) => (
                  <Tab
                    key={role}
                    value={role}
                    label={`${role} (${recipients.filter((r) => (r.roles || []).includes(role)).length})`}
                  />
                ))}
              </Tabs>
            )}

            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
              {recipientsForRole.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', px: 1, py: 2 }}>
                  No {activeRole?.toLowerCase()} for this patient.
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {recipientsForRole.map((r) => {
                    const noChannel = !r.hasWhatsapp && !r.hasEmail
                    return (
                      <Box
                        key={r.practitionerId}
                        onClick={() => !noChannel && setSelectedId(r.practitionerId)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1,
                          py: 0.75,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: selectedId === r.practitionerId ? 'rgba(67,56,202,0.6)' : 'rgba(148,163,184,0.28)',
                          backgroundColor: selectedId === r.practitionerId ? 'rgba(67,56,202,0.06)' : 'transparent',
                          cursor: noChannel ? 'not-allowed' : 'pointer',
                          opacity: noChannel ? 0.55 : 1,
                        }}
                      >
                        <Radio size="small" checked={selectedId === r.practitionerId} disabled={noChannel} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 650, fontSize: 14 }} noWrap>
                              {r.name}
                            </Typography>
                            {r.isTriggering && (
                              <Chip
                                size="small"
                                label="Referring doctor"
                                sx={{ height: 18, fontSize: 10.5, fontWeight: 700, bgcolor: 'rgba(16,185,129,0.14)', color: '#047857' }}
                              />
                            )}
                          </Stack>
                          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                            {(r.roles || []).join(', ')}
                            {noChannel ? ' · no contact on file' : ''}
                          </Typography>
                        </Box>
                      </Box>
                    )
                  })}
                </Stack>
              )}
            </Box>

            <ToggleButtonGroup
              value={deliveryMode}
              exclusive
              size="small"
              fullWidth
              onChange={(_, value) => value && setDeliveryMode(value)}
            >
              <ToggleButton value="link" sx={{ textTransform: 'none', fontWeight: 650, gap: 0.5 }}>
                <LinkRoundedIcon sx={{ fontSize: 17 }} /> Link
              </ToggleButton>
              <ToggleButton value="attachment" sx={{ textTransform: 'none', fontWeight: 650, gap: 0.5 }}>
                <AttachFileRoundedIcon sx={{ fontSize: 16 }} /> Attachment
              </ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ borderRadius: 2, border: '1px solid rgba(148,163,184,0.24)', px: 1.5, py: 0.25 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} />}
                label={<span style={{ fontSize: 14 }}>WhatsApp</span>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={email} onChange={(e) => setEmail(e.target.checked)} />}
                label={<span style={{ fontSize: 14 }}>Email</span>}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(15,23,42,0.55)' }}>
              Sent from AUMHUM. Attachment sends the file inline on WhatsApp when possible; both always include a
              secure link.
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          disabled={sending || loading || !selected || (!whatsapp && !email)}
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            backgroundColor: '#4338ca',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#3730a3', boxShadow: 'none' },
          }}
        >
          {sending ? 'Sending…' : 'Send from AUMHUM'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function rolesFromRecipients(list) {
  const seen = []
  list.forEach((r) => (r.roles || []).forEach((role) => !seen.includes(role) && seen.push(role)))
  return seen
}
